import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  Plus,
  Search,
  Star,
  Lightbulb,
  Loader2,
  X,
  Sparkles,
  FileText,
  Download,
  Trash2,
  Upload,
  CheckSquare,
  Eye,
  Pencil,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from '@/components/ui/toaster'
import { api } from '@/lib/api'
import { readDocumentLikeFile } from '@/lib/documentInput'
import { IdeaCard } from '@/components/creative-lab/IdeaCard'
import { IdeaDetailModal } from '@/components/creative-lab/IdeaDetailModal'
import { MarkdownArticle } from '@/components/creative-lab/MarkdownArticle'
import { ResearchProgressModal } from '@/components/creative-lab/ResearchProgressModal'
import { useResearchStream } from '@/hooks/useResearchStream'
import type {
  Idea,
  IdeaCreate,
  ResearchResultPayload,
  ResearchStartRequestPayload,
  ResearchStreamEvent,
  ResearchTab,
  ResearchTool,
} from '@/types'

const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'foreplay', label: 'Ads Research' },
  { value: 'perplexity', label: 'Web Research' },
  { value: 'deep_research', label: 'Deep Research' },
  { value: 'document', label: 'Document Analysis' },
  { value: 'vertical_ideas_novel', label: 'Novel Ideas' },
  { value: 'vertical_ideas_ecommerce', label: 'Ecom Ideas' },
  { value: 'vertical_ideas_finance', label: 'Finance Ideas' },
  { value: 'manual', label: 'Manual' },
]

const SORT_OPTIONS = [
  { value: 'created_at_desc', label: 'Newest' },
  { value: 'unused_first', label: 'Unused First' },
  { value: 'favorited_first', label: 'Favorites First' },
]

const TAB_ITEMS: Array<{ key: ResearchTab; label: string; subtitle: string }> = [
  { key: 'all', label: 'All Ideas', subtitle: 'View and manage all ideas' },
  { key: 'ads', label: 'Ads Research', subtitle: 'Competitor ad evidence to ideas' },
  { key: 'web', label: 'Web Research', subtitle: 'Market trend evidence to ideas' },
  { key: 'deep', label: 'Deep Research', subtitle: 'Strategic synthesis to ideas' },
  { key: 'doc', label: 'Document Analysis', subtitle: 'Extract ideas from your documents' },
]

const TOOL_OPTIONS: Array<{ value: ResearchTool; label: string; subtitle: string }> = [
  { value: 'foreplay', label: 'Ads Research', subtitle: 'Search competitor ads & analyze' },
  { value: 'perplexity', label: 'Web Research', subtitle: 'Market trends & insights' },
  { value: 'deep_research', label: 'Deep Research', subtitle: 'Long-form strategic synthesis' },
]

const DEFAULT_TOOLS_BY_TAB: Record<ResearchTab, ResearchTool[]> = {
  all: ['foreplay', 'perplexity'],
  ads: ['foreplay'],
  web: ['perplexity'],
  deep: ['deep_research'],
  doc: ['document'],
}

const TOOL_DISPLAY_MAP: Record<string, string> = {
  foreplay: 'Ads Research',
  perplexity: 'Web Research',
  deep_research: 'Deep Research',
  document: 'Document Analysis',
}

function formatToolList(tools: string[]): string {
  return tools
    .map((tool) => TOOL_DISPLAY_MAP[tool] || tool.replace(/_/g, ' '))
    .join(', ')
}

function sanitizeResearchText(text: string): string {
  return text
    .replace(/\bforeplay\b/gi, 'Ads Research')
    .replace(/\bperplexity\b/gi, 'Web Research')
    .replace(/\bdeep_research\b/gi, 'Deep Research')
    .replace(/o3[-_ ]?deep[-_ ]?research/gi, 'deep research model')
    .replace(/\bo3\b/gi, 'deep research model')
}

function extractReportTitleFromMarkdown(markdown: string): string | null {
  if (!markdown.trim()) return null
  const lines = markdown.split('\n')
  for (const raw of lines) {
    const line = raw.trim()
    if (!line) continue
    if (line.startsWith('#')) {
      const heading = line.replace(/^#+\s*/, '').replace(/\s+/g, ' ').trim()
      return heading || null
    }
    const plain = line
      .replace(/[*_`>#-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    return plain || null
  }
  return null
}

function normalizeTab(raw: string | null): ResearchTab {
  if (raw === 'ads' || raw === 'web' || raw === 'deep' || raw === 'all' || raw === 'doc') {
    return raw
  }
  return 'all'
}

function resolveSourceByTab(tab: ResearchTab, source: string): string | undefined {
  if (tab === 'ads') return 'foreplay'
  if (tab === 'web') return 'perplexity'
  if (tab === 'deep') return 'deep_research'
  if (tab === 'doc') return 'document'
  return source || undefined
}

export function IdeasPage() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const tab = normalizeTab(searchParams.get('tab'))
  const productIdParam = searchParams.get('product_id') || ''
  const sourceParam = searchParams.get('source') || searchParams.get('idea_source') || ''
  const tagParam = searchParams.get('tag') || searchParams.get('idea_tag') || ''

  const [productId, setProductId] = useState(productIdParam)
  const [source, setSource] = useState(sourceParam)
  const [tag, setTag] = useState(tagParam)
  const [favoritedOnly, setFavoritedOnly] = useState(false)
  const [sort, setSort] = useState('created_at_desc')
  const [searchQuery, setSearchQuery] = useState('')
  const [offset, setOffset] = useState(0)
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedTools, setSelectedTools] = useState<ResearchTool[]>(DEFAULT_TOOLS_BY_TAB[tab])
  const [activeResearchTools, setActiveResearchTools] = useState<ResearchTool[]>([])
  const [researchDirection, setResearchDirection] = useState('')
  const [documentContent, setDocumentContent] = useState('')
  const [documentFileName, setDocumentFileName] = useState<string | null>(null)
  const [docPreview, setDocPreview] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showResearchModal, setShowResearchModal] = useState(false)
  const [researchTaskId, setResearchTaskId] = useState<string | null>(null)
  const [streamEvents, setStreamEvents] = useState<ResearchStreamEvent[]>([])
  const [researchResult, setResearchResult] = useState<ResearchResultPayload | null>(null)
  const [isLoadingResult, setIsLoadingResult] = useState(false)
  const [viewReportMarkdown, setViewReportMarkdown] = useState<string | null>(null)
  const [viewReportTitle, setViewReportTitle] = useState<string>('Saved Report')
  const fetchedResultTaskIdRef = useRef<string | null>(null)
  const seenStreamEventIdsRef = useRef<Set<number>>(new Set())

  const limit = 48
  const resolvedSource = resolveSourceByTab(tab, source)

  useEffect(() => {
    setSelectedTools(DEFAULT_TOOLS_BY_TAB[tab])
    if (tab !== 'all') {
      setSource('')
    }
    setOffset(0)
    setSelectionMode(false)
    setSelectedIds(new Set())
  }, [tab])

  useEffect(() => {
    setProductId((current) => (current === productIdParam ? current : productIdParam))
    setOffset(0)
    setSelectionMode(false)
    setSelectedIds(new Set())
  }, [productIdParam])

  useEffect(() => {
    setSource((current) => (current === sourceParam ? current : sourceParam))
  }, [sourceParam])

  useEffect(() => {
    setTag((current) => (current === tagParam ? current : tagParam))
  }, [tagParam])

  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.getProducts(0, 100),
  })

  const { data: tags } = useQuery({
    queryKey: ['creative-lab-tags'],
    queryFn: () => api.getCreativeLabTags(),
  })

  const { data: ideas, isLoading } = useQuery({
    queryKey: ['ideas', productId, resolvedSource, tag, favoritedOnly, sort, offset],
    queryFn: () =>
      api.getIdeas(productId, {
        offset,
        limit,
        source: resolvedSource,
        tag: tag || undefined,
        favorited: favoritedOnly || undefined,
        sort: sort || undefined,
      }),
    enabled: !!productId,
  })

  const { data: reports } = useQuery({
    queryKey: ['creative-lab-reports', productId],
    queryFn: () => api.listCreativeLabResearchReports(productId, 0, 20),
    enabled: !!productId,
  })

  const filteredItems = useMemo(() => {
    return ideas?.items.filter((idea) => {
      if (!searchQuery) return true
      return idea.title.toLowerCase().includes(searchQuery.toLowerCase())
    })
  }, [ideas?.items, searchQuery])

  const toggleFavoriteMutation = useMutation({
    mutationFn: (idea: Idea) => api.toggleIdeaFavorite(idea.id, !idea.is_favorited),
    onSuccess: (updatedIdea) => {
      queryClient.invalidateQueries({ queryKey: ['ideas'] })
      if (selectedIdea?.id === updatedIdea.id) {
        setSelectedIdea(updatedIdea)
      }
      toast({ title: updatedIdea.is_favorited ? 'Added to favorites' : 'Removed from favorites' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (idea: Idea) => api.deleteIdea(idea.id),
    onSuccess: (_data, deletedIdea) => {
      queryClient.invalidateQueries({ queryKey: ['ideas'] })
      if (selectedIdea?.id === deletedIdea.id) {
        setSelectedIdea(null)
      }
      toast({ title: 'Idea deleted' })
    },
    onError: () => {
      toast({ title: 'Failed to delete idea', variant: 'destructive' })
    },
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteIdeas(ids),
    onSuccess: (_data, deletedIds) => {
      queryClient.invalidateQueries({ queryKey: ['ideas'] })
      setSelectionMode(false)
      setSelectedIds(new Set())
      toast({ title: `Deleted ${deletedIds.length} ideas` })
    },
    onError: () => {
      toast({ title: 'Failed to delete ideas', variant: 'destructive' })
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: IdeaCreate) => api.createIdea(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ideas'] })
      setShowCreateModal(false)
      toast({ title: 'Idea created' })
    },
    onError: () => {
      toast({ title: 'Failed to create idea', variant: 'destructive' })
    },
  })

  const fetchResult = useCallback(
    async (taskId: string, showToast = false) => {
      setIsLoadingResult(true)
      try {
        const data = await api.getCreativeLabResearchResult(taskId)
        setResearchResult(data)
        queryClient.invalidateQueries({ queryKey: ['ideas'] })
        if (showToast) {
          const generatedCount = data.ideas.length
          toast({
            title: 'Research complete',
            description: `${generatedCount} ideas generated across ${formatToolList(data.sources_used) || 'selected tools'}.`,
          })
        }
      } catch {
        toast({ title: 'Failed to fetch research result', variant: 'destructive' })
        if (fetchedResultTaskIdRef.current === taskId) {
          fetchedResultTaskIdRef.current = null
        }
      } finally {
        setIsLoadingResult(false)
      }
    },
    [queryClient]
  )

  const startResearchMutation = useMutation({
    mutationFn: () => {
      const payload: ResearchStartRequestPayload = {
        product_id: productId,
        tab,
        tools: selectedTools,
        research_direction: researchDirection.trim() || undefined,
      }
      if (tab === 'doc') {
        payload.document_content = documentContent
      }
      return api.startCreativeLabResearch(payload)
    },
    onMutate: () => {
      setActiveResearchTools([...selectedTools])
      setStreamEvents([])
      seenStreamEventIdsRef.current = new Set()
    },
    onSuccess: (data) => {
      setResearchTaskId(data.task_id)
      setResearchResult(null)
      fetchedResultTaskIdRef.current = null
      setShowResearchModal(true)
    },
    onError: () => {
      toast({ title: 'Failed to start research', variant: 'destructive' })
    },
  })

  const deleteReportMutation = useMutation({
    mutationFn: (reportId: string) => api.deleteCreativeLabResearchReport(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creative-lab-reports'] })
      toast({ title: 'Report deleted' })
    },
    onError: () => {
      toast({ title: 'Failed to delete report', variant: 'destructive' })
    },
  })

  const loadReportMarkdownMutation = useMutation({
    mutationFn: async (reportId: string) => {
      const [detail, markdown] = await Promise.all([
        api.getCreativeLabResearchReport(reportId),
        api.getCreativeLabResearchReportMarkdown(reportId),
      ])
      return { detail, markdown: markdown.markdown }
    },
    onSuccess: (data) => {
      const extractedTitle = extractReportTitleFromMarkdown(data.markdown)
      const preferredTitle = extractedTitle || data.detail.summary || `Saved Report (${data.detail.created_at})`
      setViewReportTitle(sanitizeResearchText(preferredTitle))
      setViewReportMarkdown(sanitizeResearchText(data.markdown))
    },
    onError: () => {
      toast({ title: 'Failed to load report markdown', variant: 'destructive' })
    },
  })

  const handleStreamEvent = useCallback(
    (event: ResearchStreamEvent) => {
      if (event.event === 'heartbeat') {
        return
      }
      if (typeof event.id === 'number') {
        if (seenStreamEventIdsRef.current.has(event.id)) {
          return
        }
        seenStreamEventIdsRef.current.add(event.id)
      }
      if (event.event === 'task_started') {
        const payloadTools = event.data.tools
        if (Array.isArray(payloadTools)) {
          const normalized = payloadTools.filter(
            (tool): tool is ResearchTool =>
              tool === 'foreplay' || tool === 'perplexity' || tool === 'deep_research' || tool === 'document'
          )
          if (normalized.length > 0) {
            setActiveResearchTools(normalized)
          }
        }
      }
      setStreamEvents((prev) => [...prev, event].slice(-300))
      if (event.event === 'completed' && researchTaskId) {
        if (fetchedResultTaskIdRef.current === researchTaskId) {
          return
        }
        fetchedResultTaskIdRef.current = researchTaskId
        void fetchResult(researchTaskId, event.event === 'completed')
      }
    },
    [fetchResult, researchTaskId]
  )

  const { isStreaming, elapsedSeconds, stop: stopStream } = useResearchStream({
    taskId: researchTaskId,
    enabled: showResearchModal && !!researchTaskId,
    onEvent: handleStreamEvent,
    onError: () => {
      toast({
        title: 'Stream disconnected',
        description: 'Connection dropped. Waiting for completion event before loading final result.',
        variant: 'destructive',
      })
    },
  })

  const toggleTool = (tool: ResearchTool) => {
    setSelectedTools((prev) =>
      prev.includes(tool) ? prev.filter((item) => item !== tool) : [...prev, tool]
    )
  }

  const closeResearchModal = () => {
    stopStream()
    setShowResearchModal(false)
  }

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      const text = await readDocumentLikeFile(file)
      setDocumentContent(text)
      setDocumentFileName(file.name)
      toast({ title: `Loaded: ${file.name}` })
    } catch {
      toast({ title: 'Failed to read file', variant: 'destructive' })
    }
    // Reset so the same file can be re-selected
    event.target.value = ''
  }, [])

  const handleFavorite = (idea: Idea) => {
    toggleFavoriteMutation.mutate(idea)
  }

  const handleDelete = (idea: Idea) => {
    if (deleteMutation.isPending) return
    if (confirm('Are you sure you want to delete this idea?')) {
      deleteMutation.mutate(idea)
    }
  }

  const [selectionMode, setSelectionMode] = useState(false)

  const exitSelectionMode = () => {
    setSelectionMode(false)
    setSelectedIds(new Set())
  }

  const handleSelect = (idea: Idea) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(idea.id)) {
        next.delete(idea.id)
      } else {
        next.add(idea.id)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    if (!filteredItems) return
    if (selectedIds.size === filteredItems.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredItems.map((i) => i.id)))
    }
  }

  const handleBulkDelete = () => {
    if (selectedIds.size === 0 || bulkDeleteMutation.isPending) return
    if (confirm(`Delete ${selectedIds.size} selected ideas?`)) {
      bulkDeleteMutation.mutate(Array.from(selectedIds))
    }
  }

  const clearFilters = () => {
    setSource('')
    setTag('')
    setFavoritedOnly(false)
    setSort('created_at_desc')
    setSearchQuery('')
    setOffset(0)
    exitSelectionMode()
  }

  const hasFilters =
    source || tag || favoritedOnly || sort !== 'created_at_desc' || searchQuery

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Product</span>
            <select
              value={productId}
              onChange={(e) => {
                const nextProductId = e.target.value
                setProductId(nextProductId)
                setOffset(0)
                exitSelectionMode()
                setSearchParams((prev) => {
                  const params = new URLSearchParams(prev)
                  params.set('tab', tab)
                  if (nextProductId) {
                    params.set('product_id', nextProductId)
                  } else {
                    params.delete('product_id')
                  }
                  return params
                })
              }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary min-w-[220px] max-w-[280px]"
            >
              <option value="">Select a product...</option>
              {products?.items.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.product_name}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-gray-500">
            Ideas tab contains All Ideas + Ads Research + Web Research + Deep Research + Document Analysis.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          {TAB_ITEMS.map((item) => (
            <button
              key={item.key}
              onClick={() =>
                setSearchParams((prev) => {
                  const params = new URLSearchParams(prev)
                  params.set('tab', item.key)
                  if (productId) {
                    params.set('product_id', productId)
                  } else {
                    params.delete('product_id')
                  }
                  return params
                })
              }
              className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                tab === item.key
                  ? 'border-emerald-400 bg-emerald-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <p className="text-sm font-medium text-gray-900">{item.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{item.subtitle}</p>
            </button>
          ))}
        </div>
      </div>

      {productId && tab !== 'doc' && (
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2.5">
              <p className="text-sm font-medium text-gray-700">Research Tools</p>
              {TOOL_OPTIONS.map((tool) => (
                <label key={tool.value} className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedTools.includes(tool.value)}
                    onChange={() => toggleTool(tool.value)}
                    disabled={startResearchMutation.isPending}
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">{tool.label}</span>
                  <span className="text-xs text-gray-400">— {tool.subtitle}</span>
                </label>
              ))}
              {selectedTools.includes('deep_research') && (
                <p className="text-xs text-amber-600">
                  Deep Research selected: generation time will increase.
                </p>
              )}
              {selectedTools.length > 0 && (
                <textarea
                  rows={2}
                  value={researchDirection}
                  onChange={(e) => setResearchDirection(e.target.value)}
                  placeholder="Optional direction for selected tools (used to guide query generation and research focus)..."
                  className="w-full max-w-2xl border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                />
              )}
              <p className="text-xs text-gray-500">
                Each channel typically generates 3-8 ideas, decided by AI based on evidence quality.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedTools(DEFAULT_TOOLS_BY_TAB[tab])}
                disabled={startResearchMutation.isPending}
              >
                Reset Tools
              </Button>
              <Button
                size="sm"
                onClick={() => startResearchMutation.mutate()}
                disabled={selectedTools.length === 0 || startResearchMutation.isPending}
                className="bg-gradient-to-r from-spectrum-emerald to-spectrum-lime hover:brightness-110 text-white"
              >
                {startResearchMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-1.5" />
                    Generate
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {productId && tab === 'doc' && (
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2.5 flex-1">
              <p className="text-sm font-medium text-gray-700">Document Analysis</p>
              <div className="flex items-center gap-3">
                <p className="text-xs text-gray-500">
                  Paste text or upload a file (.txt, .md, .doc, .docx) to extract creative ad ideas.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.md,.doc,.docx"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={startResearchMutation.isPending}
                >
                  <Upload className="h-4 w-4 mr-1.5" />
                  Upload File
                </Button>
              </div>
              {documentFileName && documentContent && (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-blue-600">
                    Loaded: {documentFileName}
                  </p>
                  <button
                    type="button"
                    onClick={() => setDocPreview(!docPreview)}
                    className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md px-2 py-0.5"
                  >
                    {docPreview ? (
                      <><Pencil className="h-3 w-3" /> Edit</>
                    ) : (
                      <><Eye className="h-3 w-3" /> Preview</>
                    )}
                  </button>
                </div>
              )}
              {docPreview && documentContent ? (
                <div className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50 overflow-auto max-h-80">
                  <MarkdownArticle markdown={documentContent} />
                </div>
              ) : (
                <textarea
                  rows={6}
                  value={documentContent}
                  onChange={(e) => {
                    setDocumentContent(e.target.value)
                    setDocumentFileName(null)
                  }}
                  placeholder="Paste your document text or markdown here..."
                  disabled={startResearchMutation.isPending}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y"
                />
              )}
            </div>
            <div className="flex items-center pt-6">
              <Button
                size="sm"
                onClick={() => startResearchMutation.mutate()}
                disabled={!documentContent.trim() || startResearchMutation.isPending}
                className="bg-gradient-to-r from-spectrum-emerald to-spectrum-lime hover:brightness-110 text-white"
              >
                {startResearchMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-1.5" />
                    Analyze Document
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {!productId && (
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-16 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
            <Lightbulb className="h-8 w-8 text-gray-400" />
          </div>
          <p className="text-gray-600 font-medium">Select a product to get started</p>
          <p className="text-sm text-gray-400 mt-1">
            Research and ideas are organized by product.
          </p>
          <p className="text-sm text-gray-400 mt-1">
            Ideas will be used as creative direction in <span className="font-semibold text-gray-500">Image & Video Freestyle</span>.
          </p>
        </div>
      )}

      {productId && (
        <>
          <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search ideas by title..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div className="h-6 w-px bg-gray-200" />

              {tab === 'all' && (
                <select
                  value={source}
                  onChange={(e) => {
                    setSource(e.target.value)
                    setOffset(0)
                  }}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  {SOURCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}

              <select
                value={tag}
                onChange={(e) => {
                  setTag(e.target.value)
                  setOffset(0)
                }}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">All Tags</option>
                {tags?.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>

              <button
                onClick={() => {
                  setFavoritedOnly(!favoritedOnly)
                  setOffset(0)
                }}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 border ${
                  favoritedOnly
                    ? 'bg-yellow-50 border-yellow-300 text-yellow-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Star
                  className={`h-3.5 w-3.5 ${
                    favoritedOnly ? 'fill-yellow-400 text-yellow-400' : ''
                  }`}
                />
                Favorites
              </button>

              <div className="h-6 w-px bg-gray-200" />

              <select
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value)
                  setOffset(0)
                }}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              {hasFilters && (
                <>
                  <div className="h-6 w-px bg-gray-200" />
                  <button
                    onClick={clearFilters}
                    className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                  >
                    <X className="h-3 w-3" />
                    Clear all
                  </button>
                </>
              )}

              <div className="ml-auto flex items-center gap-2">
                <Button
                  size="sm"
                  variant={selectionMode ? 'default' : 'outline'}
                  onClick={() => selectionMode ? exitSelectionMode() : setSelectionMode(true)}
                >
                  <CheckSquare className="h-4 w-4 mr-1.5" />
                  {selectionMode ? 'Cancel' : 'Select'}
                </Button>
                <Button size="sm" onClick={() => setShowCreateModal(true)}>
                  <Plus className="h-4 w-4 mr-1.5" />
                  New Idea
                </Button>
              </div>
            </div>
          </div>

          {selectionMode && (
            <div className="bg-blue-50 rounded-xl border border-blue-200 shadow-sm px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-blue-800">
                  {selectedIds.size} selected
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSelectAll}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  {filteredItems && selectedIds.size === filteredItems.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleBulkDelete}
                disabled={selectedIds.size === 0 || bulkDeleteMutation.isPending}
              >
                {bulkDeleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-1.5" />
                )}
                Delete {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
              </Button>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {TAB_ITEMS.find((item) => item.key === tab)?.label || 'Ideas'}
                {ideas?.total ? ` (${ideas.total})` : ''}
              </h2>
            </div>

            {isLoading ? (
              <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-3 p-4">
                {Array.from({ length: 12 }).map((_, index) => (
                  <div key={index} className="rounded-lg border border-gray-200 overflow-hidden break-inside-avoid mb-3">
                    {index % 3 !== 0 && <Skeleton className="aspect-video w-full" />}
                    <div className="p-3 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-full" />
                      <Skeleton className="h-3 w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredItems && filteredItems.length > 0 ? (
              <>
                <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-3 p-4">
                  {filteredItems.map((idea) => (
                    <IdeaCard
                      key={idea.id}
                      idea={idea}
                      onFavorite={handleFavorite}
                      onDelete={handleDelete}
                      isDeleting={deleteMutation.isPending && deleteMutation.variables?.id === idea.id}
                      onClick={setSelectedIdea}
                      selectionMode={selectionMode}
                      selected={selectedIds.has(idea.id)}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>

                {ideas && ideas.total > limit && (
                  <div className="p-4 border-t flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={offset === 0}
                      onClick={() => setOffset(Math.max(0, offset - limit))}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-500">
                      Page {Math.floor(offset / limit) + 1} of {Math.ceil(ideas.total / limit)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={offset + limit >= ideas.total}
                      onClick={() => setOffset(offset + limit)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="p-16 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                  <Lightbulb className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium">No ideas yet</p>
                <p className="text-sm text-gray-400 mt-1">
                  Run research or create one manually.
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Ideas will be used as creative direction in <span className="font-semibold text-gray-500">Image & Video Freestyle</span>.
                </p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Saved Reports</h2>
            </div>
            <div className="p-4 space-y-2">
              {reports?.items.length ? (
                reports.items.map((report) => (
                  <div
                    key={report.id}
                    className="rounded-lg border border-gray-200 p-3 flex flex-wrap items-center gap-3"
                  >
                    <div className="min-w-[220px]">
                      <p className="text-sm font-medium text-gray-900">
                        {sanitizeResearchText(report.summary || 'Saved report')}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatToolList(report.tools)} · ideas {report.total_ideas} · {report.created_at}
                      </p>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => loadReportMarkdownMutation.mutate(report.id)}
                      >
                        <FileText className="h-4 w-4 mr-1.5" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteReportMutation.mutate(report.id)}
                        disabled={deleteReportMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 mr-1.5" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No saved reports for this product.</p>
              )}
            </div>
          </div>
        </>
      )}

      <IdeaDetailModal
        idea={selectedIdea}
        open={!!selectedIdea}
        onClose={() => setSelectedIdea(null)}
        onFavorite={handleFavorite}
        onDelete={handleDelete}
        isDeleting={deleteMutation.isPending && deleteMutation.variables?.id === selectedIdea?.id}
      />

      {showCreateModal && (
        <CreateIdeaModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isSubmitting={createMutation.isPending}
        />
      )}

      <ResearchProgressModal
        open={showResearchModal}
        tools={activeResearchTools}
        elapsedSeconds={elapsedSeconds}
        isStreaming={isStreaming}
        events={streamEvents}
        result={researchResult}
        isLoadingResult={isLoadingResult}
        onClose={closeResearchModal}
      />

      {viewReportMarkdown && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setViewReportMarkdown(null)}>
          <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden" onClick={(event) => event.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <p className="text-sm font-medium text-gray-900">{viewReportTitle}</p>
              <div className="flex items-center gap-2">
                <a
                  className="inline-flex items-center text-xs px-2 py-1 border rounded-md hover:bg-gray-50"
                  href={`data:text/markdown;charset=utf-8,${encodeURIComponent(viewReportMarkdown)}`}
                  download="creative-lab-report.md"
                >
                  <Download className="h-3.5 w-3.5 mr-1" />
                  Download
                </a>
                <button className="text-gray-500 hover:text-gray-700" onClick={() => setViewReportMarkdown(null)}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-auto max-h-[78vh]">
              <MarkdownArticle markdown={viewReportMarkdown} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function CreateIdeaModal({
  onClose,
  onSubmit,
  isSubmitting,
}: {
  onClose: () => void
  onSubmit: (data: IdeaCreate) => void
  isSubmitting: boolean
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [tagsInput, setTagsInput] = useState('')

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!title.trim() || !description.trim()) return

    const tags = tagsInput
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)

    onSubmit({
      title: title.trim(),
      description: description.trim(),
      source: 'manual',
      tags: tags.length > 0 ? tags : undefined,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">New Idea</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Give your idea a name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
              required
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={4}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Describe the creative idea, concept, or inspiration..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none text-sm"
              required
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Tags <span className="text-gray-400 font-normal">(comma-separated)</span>
            </label>
            <input
              type="text"
              value={tagsInput}
              onChange={(event) => setTagsInput(event.target.value)}
              placeholder="e.g. conversion, social-proof"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
              disabled={isSubmitting}
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!title.trim() || !description.trim() || isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-1.5" />
              )}
              Create Idea
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
